import { refreshTokenHandler } from '@brightdrop/auth-client';

import {
  captureSidekickActingOrganizationScope,
  type SidekickActingOrganizationScope,
  withActingOrganizationHeader,
} from '../api/actingOrganization';
import type { SidekickError } from '../state/chatTypes';
import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';
import { parseErrorBody } from './sidekickErrorBody';

/** Bounded back-off before retrying GET/POST when the BE returns HTTP 409. */
export const SIDEKICK_HTTP_CONFLICT_BACKOFF_MS = 500;
export const SIDEKICK_TRANSPORT_TIMEOUT_MS = 60_000;

/**
 * Spec-documented BE error codes (Sidekick Auth / conversations API).
 * Transport layers classify HTTP bodies against this set; synthetic
 * FE-only codes live in `state/constants.ts`.
 */
export const SIDEKICK_SPEC_ERROR_CODES = [
  'INVALID_REQUEST',
  'INVALID_QUERY',
  'OUT_OF_DOMAIN',
  'UNSAFE_QUERY',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'RATE_LIMITED',
  'INTERNAL',
  'SQL_EXECUTION',
  'RAG_RETRIEVAL',
  'LLM_ERROR',
  'SERVICE_UNAVAILABLE',
  'TIMEOUT',
  'ORGANIZATION_NOT_ONBOARDED',
] as const;

export type SidekickSpecErrorCode = (typeof SIDEKICK_SPEC_ERROR_CODES)[number];

/** Thrown when a Sidekick HTTP call fails after bounded auth/conflict retries. */
export class SidekickHttpError extends Error {
  readonly status: number;
  readonly sidekickError: SidekickError;

  constructor(status: number, sidekickError: SidekickError) {
    super(sidekickError.message);
    this.name = 'SidekickHttpError';
    this.status = status;
    this.sidekickError = sidekickError;
  }
}

const resolveBearerToken = async (): Promise<string> => {
  const tokenResponse = await refreshTokenHandler();
  const token = tokenResponse?.accessToken;
  if (token === undefined || token === '') {
    throw new Error('Sidekick transport: missing bearer token');
  }
  return token;
};

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', abortSleep);
      resolve();
    }, ms);
    const abortSleep = () => {
      clearTimeout(timeoutId);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', abortSleep, { once: true });
  });

const buildSyntheticTransportTimeoutError = (message: string) =>
  new SidekickHttpError(0, {
    code: SYNTHETIC_TRANSPORT_ERROR_CODE,
    message,
  });

/** Applies a bounded Sidekick transport timeout to a single async operation. */
export const withSidekickTransportTimeout = async <T>({
  operation,
  message,
  onTimeout,
  timeoutMs = SIDEKICK_TRANSPORT_TIMEOUT_MS,
}: {
  operation: Promise<T>;
  message: string;
  onTimeout?: () => void;
  timeoutMs?: number;
}): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(buildSyntheticTransportTimeoutError(message));
      onTimeout?.();
    }, timeoutMs);
  });

  try {
    return await Promise.race([operation, timeout]);
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  }
};

export type FetchWithSidekickAuthParams = {
  url: string;
  signal: AbortSignal;
  init?: Omit<RequestInit, 'headers' | 'signal'> & {
    headers?: Record<string, string>;
  };
  /**
   * The organization to send the request for, when it was captured earlier
   * than the request is made. Defaults to the current one.
   */
  organizationScope?: SidekickActingOrganizationScope;
};

/**
 * Issues an authenticated `fetch` with JWT Bearer via `refreshTokenHandler`,
 * plus `x-bd-organization` while the user is acting as another organization
 * (no `X-User-Id` / role / session / request-id headers — the proxy derives
 * those from the token).
 *
 * Recovery:
 * - Acting organization is captured once and reused across every attempt
 * - Bearer token is resolved with `refreshTokenHandler` before each attempt
 * - HTTP 401 → resolve a fresh bearer token and retry once
 * - HTTP 409 → bounded single retry after {@link SIDEKICK_HTTP_CONFLICT_BACKOFF_MS}
 * - other non-2xx → {@link SidekickHttpError} with classified body
 */
export const fetchWithSidekickAuth = async (
  params: FetchWithSidekickAuthParams
): Promise<Response> => {
  const { url, signal, init } = params;
  const organizationScope =
    params.organizationScope ?? captureSidekickActingOrganizationScope();
  let refreshedOnce = false;
  let conflictRetried = false;

  while (true) {
    const token = await resolveBearerToken();
    const headers: Record<string, string> = {
      ...withActingOrganizationHeader(
        init?.headers,
        organizationScope.actingOrganizationId
      ),
      Authorization: `Bearer ${token}`,
    };

    let response: Response;
    const requestController = new AbortController();
    const abortRequest = () => {
      requestController.abort();
    };
    signal.addEventListener('abort', abortRequest, { once: true });
    if (signal.aborted) {
      abortRequest();
    }

    try {
      response = await withSidekickTransportTimeout({
        operation: fetch(url, {
          ...init,
          headers,
          signal: requestController.signal,
        }),
        message: 'Sidekick transport: request timed out',
        onTimeout: abortRequest,
      });
    } catch (networkErr) {
      if (networkErr instanceof SidekickHttpError) {
        throw networkErr;
      }
      throw new SidekickHttpError(0, {
        code: SYNTHETIC_TRANSPORT_ERROR_CODE,
        message:
          networkErr instanceof Error ? networkErr.message : String(networkErr),
      });
    } finally {
      signal.removeEventListener('abort', abortRequest);
    }

    if (response.ok) {
      return response;
    }

    if (response.status === 401 && !refreshedOnce) {
      refreshedOnce = true;
      continue;
    }

    if (response.status === 409 && !conflictRetried) {
      conflictRetried = true;
      await sleep(SIDEKICK_HTTP_CONFLICT_BACKOFF_MS, signal);
      continue;
    }

    signal.addEventListener('abort', abortRequest, { once: true });
    if (signal.aborted) {
      abortRequest();
    }
    try {
      const sidekickError = await withSidekickTransportTimeout({
        operation: parseErrorBody(response),
        message: 'Sidekick transport: error body timed out',
        onTimeout: abortRequest,
      });
      throw new SidekickHttpError(response.status, sidekickError);
    } finally {
      signal.removeEventListener('abort', abortRequest);
    }
  }
};
